import { AccountType, IAccount, IAccountMappingTemplateGroup, IGroupedAccount, IGroupedAccountDefition, ItemType, ReclassificationStatement } from "@modeso/types__aumico-lib-annualreport-be";
import { IGroupingMasking } from '@modeso/types__aumico-lib-annualreport-be/dist/models/interfaces/IGroupingMasking';
import { cloneDeep } from 'lodash-es';
import { v4 as uuidv4 } from 'uuid';
import { GroupingPathHelper } from './GroupingPathHelper';
import { MaskingHelper } from "./MaskingHelper";
import { SplitAccountHelper } from './SplitAccountHelper';

/**
 * REV-20899: session-scoped shape for a "pending" phantom reclasses account
 * the user has created in grouping/masking but has not yet saved into
 * `finStatements`. Used purely as input to `applyNewReclassesAccounts` to
 * inject the entry into a cloned template's matching FLI `reclassesAccounts`
 * array, either for in-step rendering or for building the AR payload at save
 * time. NOT persisted on `IGroupingMasking` — phantoms ultimately live as
 * regular `IReclassesAccounts` entries on `IFinStatements`.
 */
export interface INewReclassesAccountSessionEntry {
    statement: ReclassificationStatement;
    fliId: string;
    /**
     * REV-21798: when set, the account is placed on this SubFLI (identified by
     * its `name`) inside `fliId` rather than directly on the FLI. Needed since
     * a reclassification can target a SubFLI.
     */
    subFliId?: string;
    /**
     * REV-21881: the G&M group the account was created INTO, when the reclassification target was
     * a group rather than the FLI/SubFLI itself.
     *
     * Placement does not need it — a phantom always lands on its host FLI's (or SubFLI's)
     * `reclassesAccounts`, and group membership is expressed by `groupedAccounts` — but the REUSE
     * check does: without it, reclassifying the same source onto a SECOND group of the same FLI
     * found the account created for the FIRST one and left the amount there (in the group rendered
     * above), instead of creating one in the group the user picked.
     */
    groupId?: string;
    accountNumber: string;
    name: string;
}
/**
 * A helper class to manage account name grouping logic
 */
export class GroupingHelper {
    public static getAllGroupedAccounts(groupedAccounts: Record<string, IGroupedAccountDefition>): string[] {
        if (!groupedAccounts) {
            return [];
        }

        const allGroupedAccountIdentifiers = Object.values(groupedAccounts).flatMap((group) => group.accounts);

        return [...new Set(allGroupedAccountIdentifiers)]; // Return unique account identifiers (generated with MaskingHelper.getAccountIdentifier)
    }

    public static applyGroupingAndSummarize(
        group: IAccountMappingTemplateGroup,
        groupedAccountsMap: Record<string, IGroupedAccountDefition>,
    ): IAccountMappingTemplateGroup {
        if (!group || !groupedAccountsMap) {
            return group;
        }
        const displayGroupIdAsAccountNumber: boolean = true


        const allGroupedAccountIdentifiers = this.getAllGroupedAccounts(groupedAccountsMap);

        /**
         * Find the group an account belongs to.
         *
         * `identifiers` are the account's identifier forms, most specific first (see
         * `MaskingHelper.getIdentifierCandidates`). They are tried IN ORDER across all groups, so a
         * row-scoped membership beats a legacy 2-part entry that stands for every instance of a
         * duplicate — which is what lets one instance of a duplicate pair be moved to another group
         * while the other one stays where the old entry put it.
         */
        const findGroupForAccount = (
            identifiers: string[],
            parentSortingId: string
        ): IGroupedAccountDefition | undefined => {
            for (const identifier of identifiers) {
                for (const groupId in groupedAccountsMap) {
                    const groupDef = groupedAccountsMap[groupId];

                    // 1. Check if the account identifier is in the group.
                    // 2. AND check if the group ID starts with the parent's sorting ID (contextual check).
                    if (
                        groupDef.accounts.includes(identifier) &&
                        groupDef.groupId.startsWith(parentSortingId)
                    ) {
                        return groupDef;
                    }
                }
            }
            return undefined;
        };

        const traverseAndMark = (item: any, parentSortingId: string) => {
            if (!item) return;

            const currentSortingId = item.sortingId || parentSortingId;

            if (item.sortedItems) { // isGroup check
                item.sortedItems?.forEach((child: any) => traverseAndMark(child, currentSortingId));
            } else if (item.type === ItemType.FLI) {
                item.accounts?.forEach((acc: any) => {
                    if (acc.type === 'SUB FLI' || acc.type === 'OF_THAT_ITEM') {
                        acc.accounts?.forEach((subAcc: any) => {
                            const identifiers = MaskingHelper.getIdentifierCandidates(subAcc);
                            if (identifiers.some((id) => allGroupedAccountIdentifiers.includes(id))) {
                                // --- : Pass SUB FLI's sorting ID (acc.sortingId)
                                const groupInfo = findGroupForAccount(identifiers, acc.sortingId);
                                if (groupInfo) {
                                    // The startsWith check is now redundant here
                                    subAcc.isGrouped = true;
                                    subAcc.groupId = groupInfo.groupId;
                                }
                            }
                        });
                    } else {
                        const identifiers = MaskingHelper.getIdentifierCandidates(acc);
                        if (identifiers.some((id) => allGroupedAccountIdentifiers.includes(id))) {
                            // --- : Pass FLI's sorting ID (item.sortingId)
                            const groupInfo = findGroupForAccount(identifiers, item.sortingId);
                            if (groupInfo) {
                                // The startsWith check is now redundant here
                                acc.isGrouped = true;
                                acc.groupId = groupInfo.groupId;
                            }
                        }
                    }
                });
            }
        };

        const traverseAndSummarize = (item: any) => {
            if (!item) return;

            if (item.sortedItems) { // isGroup check
                item.sortedItems?.forEach((child: any) => traverseAndSummarize(child));
            } else if (item.type === ItemType.FLI || item.type === 'SUB FLI' || item.type === 'OF_THAT_ITEM') {

                // ---  START: Recursively summarize Sub-FLIs ---
                // If this is an FLI, check its accounts for any Sub-FLIs and process them first
                if (item.accounts) {
                    item.accounts.forEach((acc: any) => {
                        if (acc.type === 'SUB FLI' || acc.type === 'OF_THAT_ITEM') {
                            traverseAndSummarize(acc);
                        }
                    });
                }
                // ---  END ---

                if (!item.accounts) return;

                const groupsInFli: Record<string, IAccount[]> = {};

                // Collect all accounts that belong to a group
                item.accounts.forEach((acc: IAccount & { isGrouped?: boolean; groupId?: string }) => {
                    if (acc.isGrouped && acc.groupId) {
                        if (!groupsInFli[acc.groupId]) {
                            groupsInFli[acc.groupId] = [];
                        }
                        groupsInFli[acc.groupId].push(acc);
                    }
                });

                // Create summary accounts and replace original accounts
                for (const groupId in groupsInFli) {
                    // ... (rest of your summarization logic remains the same)
                    const groupedAccounts = groupsInFli[groupId];
                    const groupInfo = groupedAccountsMap[groupId];

                    if (groupedAccounts.length > 0) {
                        // Find the position of the first account in this group BEFORE filtering
                        const firstAccountIndex = item.accounts.findIndex((acc: IAccount & { groupId?: string }) => acc.groupId === groupId);

                        const summaryAccount: IGroupedAccount = {
                            accountNumber: displayGroupIdAsAccountNumber == true ? groupInfo.groupId : uuidv4(),
                            name: groupInfo.groupName,
                            type: groupedAccounts[0].type,
                            openingBalance: groupedAccounts.reduce((sum, acc) => sum + acc.openingBalance, 0),
                            closingBalance: groupedAccounts.reduce((sum, acc) => sum + acc.closingBalance, 0),
                            calculatedOpeningBalance: groupedAccounts.reduce((sum, acc) => sum + (acc.calculatedOpeningBalance || acc.openingBalance), 0),
                            calculatedClosingBalance: groupedAccounts.reduce((sum, acc) => sum + (acc.calculatedClosingBalance || acc.closingBalance), 0),
                            openingReClass: groupedAccounts.reduce((sum, acc) => sum + (acc.openingReClass || 0), 0),
                            openingAdjustment: groupedAccounts.reduce((sum, acc) => sum + (acc.openingAdjustment || 0), 0),
                            closingReClass: groupedAccounts.reduce((sum, acc) => sum + (acc.closingReClass || 0), 0),
                            closingAdjustment: groupedAccounts.reduce((sum, acc) => sum + (acc.closingAdjustment || 0), 0),
                            accounts: groupedAccounts,
                            groupName: groupInfo.groupName,
                            groupId: groupId,
                        };
                        // A translated print copy renamed the group (TemplateTranslatorVisitor); the saved
                        // sort order still knows the row by its report-language name.
                        const originalGroupName = (groupInfo as { originalGroupName?: string }).originalGroupName;
                        if (originalGroupName != null) {
                            (summaryAccount as IGroupedAccount & { originalName?: string }).originalName = originalGroupName;
                        }

                        // Remove original accounts
                        item.accounts = item.accounts.filter((acc: IAccount & { groupId?: string }) => acc.groupId !== groupId);

                        // Insert summary account at the position where the first account was
                        if (firstAccountIndex !== -1 && firstAccountIndex <= item.accounts.length) {
                            item.accounts.splice(firstAccountIndex, 0, summaryAccount);
                        } else {
                            // Fallback: add to end if position not found
                            item.accounts.push(summaryAccount);
                        }
                    }
                }
            }
        };

        traverseAndMark(group, group["sortingId"]);
        traverseAndSummarize(group);

        return group;
    }

    public static recalculateGroupSummaries(group: IAccountMappingTemplateGroup): IAccountMappingTemplateGroup {
        if (!group) {
            return group;
        }

        const traverse = (item: any) => {
            if (!item) {
                return;
            }

            // If the item itself is a split account, do not perform any calculations on it.
            if (item.isGroupedSplitAccount || item.isGroupedSplitAccountCounterpart) {
                return;
            }

            // Use a bottom-up approach. First, recurse through children if they exist.
            if (item.sortedItems) { // This is a branch node (a group of groups)
                item.sortedItems.forEach(traverse);
            } else if (item.accounts) { // This is a leaf-like node that contains accounts (e.g., an FLI)
                // If any accounts are themselves groups, recurse into them.
                item.accounts.forEach(traverse);
            }
            // REV-20899: an FLI / SubFLI may also carry phantom reclasses
            // accounts on `reclassesAccounts`. These have no nested children
            // of their own — they are leaves carrying only an
            // `openingReClass` / `closingReClass`. We still walk them here for
            // symmetry in case any future engine attaches state to them.
            if (Array.isArray(item.reclassesAccounts)) {
                item.reclassesAccounts.forEach(traverse);
            }

            // After children have been calculated, sum them up for the current item.
            let itemsToSum: any[] = [];
            if (item.sortedItems) {
                itemsToSum = item.sortedItems.filter((child: any) => child.type !== ItemType.SUB_TOTAL);
            } else if (item.accounts) {
                itemsToSum = item.accounts;
            }
            // REV-20899: phantom reclasses accounts contribute to their host
            // FLI / SubFLI aggregate. Their imported balances are zero, so
            // they only affect `openingReClass` / `closingReClass` in
            // practice — but adding them to `itemsToSum` is the right
            // mathematical model and keeps every aggregate field consistent.
            if (Array.isArray(item.reclassesAccounts) && item.reclassesAccounts.length > 0) {
                itemsToSum = itemsToSum.concat(item.reclassesAccounts);
            }

            // Only perform the calculation if the item has balance properties and items to sum.
            if (item.calculatedOpeningBalance !== undefined && itemsToSum.length > 0) {
                item.openingBalance = itemsToSum.reduce((sum: number, child: any) => sum + (child.openingBalance || 0), 0);
                item.closingBalance = itemsToSum.reduce((sum: number, child: any) => sum + (child.closingBalance || 0), 0);
                item.calculatedOpeningBalance = itemsToSum.reduce((sum: number, child: any) => sum + (child.calculatedOpeningBalance || child.openingBalance || 0), 0);
                item.calculatedClosingBalance = itemsToSum.reduce((sum: number, child: any) => sum + (child.calculatedClosingBalance || child.closingBalance || 0), 0);
                // REV-20899: also aggregate transactional reclass sums so any
                // aggregate node (FLI / SubFLI / group / section) carries the
                // total of its children's `openingReClass` / `closingReClass`,
                // including any phantom reclasses accounts merged into
                // `itemsToSum` just above.
                item.openingReClass = itemsToSum.reduce((sum: number, child: any) => sum + (child.openingReClass || 0), 0);
                item.closingReClass = itemsToSum.reduce((sum: number, child: any) => sum + (child.closingReClass || 0), 0);
            }
        };

        traverse(group);
        return group;
    }

    /**
     * Applies all grouping and masking transformations to a template group.
     * This method replicates the transformation logic used in print mappers.
     * 
     * @param template The template group to transform
     * @param groupingAndMasking The grouping and masking configuration
     * @param applyTransformations Whether to apply transformations (typically from printOptions.displayOptions.applyGroupingMasking)
     * @returns The transformed template group
     */
    public static applyAllGroupingMaskingTransformations(
        template: IAccountMappingTemplateGroup,
        groupingAndMasking: IGroupingMasking | undefined,
        applyTransformations: boolean
    ): IAccountMappingTemplateGroup {
        if (!applyTransformations || !groupingAndMasking) {
            return template;
        }

        // 0. REV-21798: hoist phantom reclasses accounts into `accounts` BEFORE
        //    anything else. Path generation, grouping and sorting all operate on
        //    `accounts` only, so a phantom that is still parked on
        //    `reclassesAccounts` at this point would get no `sortingId`, could
        //    not be a member of a group, and would ignore the saved order.
        //    Notes consume the tree this method returns (notes.page.ts →
        //    FlisMapper), so hoisting here is what makes a manual account show
        //    up inside its group, in the right position, in the Anhang.
        const cloned = cloneDeep(template);
        GroupingHelper.hoistReclassesAccounts(cloned);

        // 1. Generate sorting paths
        let transformed = GroupingPathHelper.generateAndAddFullSortingState(
            cloned,
            ''
        );

        // 2. Apply grouping
        if (groupingAndMasking.groupedAccounts) {
            transformed = GroupingHelper.applyGroupingAndSummarize(
                transformed,
                groupingAndMasking.groupedAccounts,
            );
        }

        // 3. Apply splitting
        if (groupingAndMasking.splitGroupAccounts) {
            transformed = SplitAccountHelper.applySplitAccounts(
                transformed,
                groupingAndMasking.splitGroupAccounts,
            );

            // 4. Recalculate summaries after splitting
            transformed = GroupingHelper.recalculateGroupSummaries(transformed);
        }

        // 5. Apply sorting to all FLI items recursively
        if (groupingAndMasking.sorting) {
            GroupingHelper.applySortingToAllItems(transformed, groupingAndMasking.sorting);
        }

        // 6. Apply masking/renaming to all accounts
        if (groupingAndMasking.renamedAccounts) {
            GroupingHelper.applyMaskingToAllAccounts(transformed, groupingAndMasking.renamedAccounts);
        }

        // REV-20899: always finalize with a summary recalculation so transactional
        // reclassifications (already stamped on leaf accounts by
        // `applyReclassificationsToFinStatements`) are aggregated up to every
        // FLI / SubFLI / group / section node — even when no split accounts
        // are present. Without this, FlisMapper would read raw FLI totals
        // and miss the child accounts' reclasses.
        transformed = GroupingHelper.recalculateGroupSummaries(transformed);

        return transformed;
    }

    /**
     * REV-20899: injects each session-pending phantom reclasses account into
     * its host FLI's `reclassesAccounts` array on the given template (which
     * must be a clone — callers must not pass a reference into the NgRx
     * store).
     *
     * Used in two places:
     *   1. The grouping/masking step's `initializeFinStatements`, to make
     *      newly-created phantoms appear in the table immediately.
     *   2. The save path (`getAnnualReportToBeSaved`), to merge the session
     *      list into the cloned `finStatements` payload that ships to the
     *      backend — at which point the phantom becomes an ordinary
     *      `IReclassesAccounts` entry like any other.
     *
     * `statementFilter` (optional): only entries matching this statement are
     * injected. Lets the caller transform each statement template
     * separately without cross-statement leakage.
     *
     * Each phantom is stamped with the statement's primary account type
     * (`BALANCE` on the BS, `INCOME` on the IS) so downstream code that
     * filters by `AccountType.BALANCE` / `INCOME` treats it as an ordinary
     * account row. "Phantom-ness" is encoded purely by membership in
     * `reclassesAccounts`, not by the `type` field. Imported balances are
     * zero — the displayed CY / PY of a phantom equals only its net
     * reclassification sum, computed elsewhere from
     * `IGroupingMasking.reclassifications`.
     *
     * Idempotent: skips entries whose `(accountNumber, name)` already
     * exists on the target FLI's `reclassesAccounts`.
     */
    public static applyNewReclassesAccounts(
        group: IAccountMappingTemplateGroup,
        newReclassesAccounts: INewReclassesAccountSessionEntry[],
        statementFilter?: ReclassificationStatement,
    ): void {
        if (!group || !Array.isArray(newReclassesAccounts) || newReclassesAccounts.length === 0) return;

        // A group node has its own `sortedItems`. An FLI does not.
        const isGroup = (item: any): boolean => item != null && 'sortedItems' in item;

        /**
         * Collect EVERY occurrence of the FLI named `fliName` anywhere in
         * the template tree.
         *
         * Why "every"? `IAccountMappingTemplateGroup` stores the same FLI in
         * two parallel arrays:
         *   • `items[]`     — the canonical persisted list.
         *   • `sortedItems[]` — the rendered/sorted view (sometimes references,
         *                       sometimes full objects).
         *
         * On a freshly-loaded AR they share references (the templates lib
         * rehydrates sortedItems pointers from items/groups), but as soon as
         * the AR is `cloneDeep`-d, those two arrays hold INDEPENDENT copies
         * of the FLI. Mutating one no longer mutates the other.
         *
         * Two consequences:
         *   1. Save path: `prepareReportForSaving` trims `sortedItems[]` down
         *      to `{name, type}` references and serializes only `items[]`.
         *      Phantoms not present on the `items[]` copy of the FLI are lost.
         *   2. In-session render: the template iterates the `sortedItems[]`
         *      copy via `fliRowEntries(item)`. Phantoms not present there
         *      never reach the DOM.
         *
         * So we inject into BOTH copies (and into nested-group copies along
         * `groups[]` and inside `sortedItems[]`).
         */
        const collectFlisByName = (g: any, fliName: string, out: any[]): void => {
            if (g == null) return;
            if (Array.isArray(g.items)) {
                for (const item of g.items) {
                    if (item != null && item.type === ItemType.FLI && item.name === fliName) {
                        out.push(item);
                    }
                }
            }
            if (Array.isArray(g.sortedItems)) {
                for (const item of g.sortedItems) {
                    if (item == null) continue;
                    if (item.type === ItemType.FLI && item.name === fliName) {
                        // sortedItems entries can be either bare references
                        // (`{name, type}`) or full FLI objects. We only push
                        // full objects — bare references have no
                        // `reclassesAccounts` slot to mutate (and after
                        // prepareReportForSaving runs they would be trimmed
                        // back to references anyway).
                        if (Array.isArray((item as any).accounts) || 'accounts' in item) {
                            out.push(item);
                        }
                    } else if (isGroup(item)) {
                        collectFlisByName(item, fliName, out);
                    }
                }
            }
            if (Array.isArray(g.groups)) {
                for (const sub of g.groups) collectFlisByName(sub, fliName, out);
            }
        };

        for (const entry of newReclassesAccounts) {
            if (entry == null || entry.fliId == null) continue;
            if (statementFilter != null && entry.statement !== statementFilter) continue;

            const flis: any[] = [];
            collectFlisByName(group, entry.fliId, flis);
            if (flis.length === 0) continue;

            const phantomType =
                entry.statement === ReclassificationStatement.BALANCE_SHEET
                    ? AccountType.BALANCE
                    : AccountType.INCOME;

            for (const fli of flis) {
                // REV-21798: a SubFLI-hosted entry lands on that SubFLI's own
                // `reclassesAccounts`, not on the parent FLI's.
                let host: any = fli;
                if (entry.subFliId != null) {
                    const subFli = (Array.isArray(fli.accounts) ? fli.accounts : []).find(
                        (a: any) =>
                            a != null &&
                            (a.type === ItemType.SUB_FLI || a.type === ItemType.OF_THAT_ITEM) &&
                            a.name === entry.subFliId,
                    );
                    if (subFli == null) continue;
                    host = subFli;
                }

                if (!Array.isArray((host as any).reclassesAccounts)) (host as any).reclassesAccounts = [];
                const list: any[] = (host as any).reclassesAccounts;

                // Idempotent: skip if a phantom with the same
                // (accountNumber, name) already exists on this FLI copy.
                const alreadyExists = list.some(
                    (p) =>
                        p != null &&
                        p.accountNumber === entry.accountNumber &&
                        (p.name ?? '') === entry.name,
                );
                if (alreadyExists) continue;

                list.push({
                    accountNumber: entry.accountNumber,
                    name: entry.name,
                    type: phantomType,
                    closingBalance: 0,
                    openingBalance: 0,
                    calculatedClosingBalance: 0,
                    calculatedOpeningBalance: 0,
                });
            }
        }
    }

    /**
     * REV-21798: remove specific reclasses accounts from a template tree,
     * identified by composite key (`accountNumber|name`).
     *
     * Distinct from `pruneOrphanedReclassesAccounts`, which drops entries that
     * nothing references. A user-deleted account can still be referenced — by
     * transactions that reclassify INTO it — and must go anyway; those
     * transactions are intentionally left dangling so they show up as
     * conflicts.
     *
     * Mutates in place — only ever call it on a clone.
     */
    public static removeReclassesAccounts(
        group: IAccountMappingTemplateGroup,
        keysToRemove: Set<string>,
    ): void {
        if (group == null || keysToRemove.size === 0) return;
        const isGroup = (item: any): boolean => item != null && 'sortedItems' in item;
        const keep = (ra: any): boolean =>
            ra != null && !keysToRemove.has(`${ra.accountNumber}|${ra.name ?? ''}`);

        const removeIn = (node: any): void => {
            if (node == null) return;
            if (Array.isArray(node.reclassesAccounts)) {
                node.reclassesAccounts = node.reclassesAccounts.filter(keep);
            }
            // Hoisted phantoms live on `accounts`; a save payload built from a
            // hoisted tree must be cleaned there too.
            if (Array.isArray(node.accounts)) {
                node.accounts = node.accounts.filter((a: any) => !a?.isReclassAccount || keep(a));
                for (const a of node.accounts) {
                    if (a != null && (a.type === ItemType.SUB_FLI || a.type === ItemType.OF_THAT_ITEM)) {
                        removeIn(a);
                    }
                }
            }
            const itemList =
                (Array.isArray(node.sortedItems) ? node.sortedItems : null) ??
                (Array.isArray(node.items) ? node.items : null);
            if (itemList) {
                for (const item of itemList) {
                    if (item == null) continue;
                    if (item.type === ItemType.FLI || isGroup(item)) removeIn(item);
                }
            }
            if (Array.isArray(node.groups)) {
                for (const sub of node.groups) removeIn(sub);
            }
        };

        removeIn(group);
    }

    /**
     * REV-20899: Returns true if the template tree contains at least one
     * `reclassesAccounts` entry whose composite key is NOT in `referencedKeys`.
     * Used to decide whether a version bump is needed on save.
     */
    public static hasOrphanedReclassesAccounts(
        group: IAccountMappingTemplateGroup | undefined | null,
        referencedKeys: Set<string>,
    ): boolean {
        if (group == null) return false;
        const isGroup = (item: any): boolean => item != null && 'sortedItems' in item;

        const checkNode = (node: any): boolean => {
            if (node == null) return false;
            if (Array.isArray(node.reclassesAccounts)) {
                if (node.reclassesAccounts.some(
                    (ra: any) => ra != null && ra.type !== AccountType.RECLASSES && !referencedKeys.has(`${ra.accountNumber}|${ra.name ?? ''}`),
                )) return true;
            }
            const itemList =
                (Array.isArray(node.sortedItems) ? node.sortedItems : null) ??
                (Array.isArray(node.items) ? node.items : null);
            if (itemList) {
                for (const item of itemList) {
                    if (item == null) continue;
                    if (item.type === ItemType.FLI || isGroup(item)) {
                        if (checkNode(item)) return true;
                    }
                }
            }
            if (Array.isArray(node.groups)) {
                for (const sub of node.groups) {
                    if (checkNode(sub)) return true;
                }
            }
            return false;
        };

        return checkNode(group);
    }

    /**
     * REV-20899: Walk a template/view-model tree and remove any `reclassesAccounts`
     * entry whose composite key (`accountNumber|name`) is NOT in `referencedKeys`.
     *
     * Used after the transaction store changes to garbage-collect phantom reclasses
     * accounts that are no longer targeted (or sourced) by any reclassification.
     * Mutates `group` in place — always call on a deep-cloned copy for save payloads.
     */
    public static pruneOrphanedReclassesAccounts(
        group: IAccountMappingTemplateGroup,
        referencedKeys: Set<string>,
    ): void {
        if (group == null) return;
        const isGroup = (item: any): boolean => item != null && 'sortedItems' in item;

        const pruneNode = (node: any): void => {
            if (node == null) return;
            if (Array.isArray(node.reclassesAccounts)) {
                node.reclassesAccounts = node.reclassesAccounts.filter(
                    (ra: any) => ra != null && (ra.type === AccountType.RECLASSES || referencedKeys.has(`${ra.accountNumber}|${ra.name ?? ''}`)),
                );
            }
            const itemList =
                (Array.isArray(node.sortedItems) ? node.sortedItems : null) ??
                (Array.isArray(node.items) ? node.items : null);
            if (itemList) {
                for (const item of itemList) {
                    if (item == null) continue;
                    if (item.type === ItemType.FLI) pruneNode(item);
                    else if (isGroup(item)) pruneNode(item);
                }
            }
            if (Array.isArray(node.groups)) {
                for (const sub of node.groups) pruneNode(sub);
            }
            // Also handle SUB_FLI / OF_THAT_ITEM children that may carry reclassesAccounts
            if (Array.isArray(node.accounts)) {
                for (const a of node.accounts) {
                    if (a != null && Array.isArray(a.reclassesAccounts)) {
                        a.reclassesAccounts = a.reclassesAccounts.filter(
                            (ra: any) => ra != null && (ra.type === AccountType.RECLASSES || referencedKeys.has(`${ra.accountNumber}|${ra.name ?? ''}`)),
                        );
                    }
                }
            }
        };

        pruneNode(group);
    }

    /**
     * REV-21798: move every *new-world* phantom reclasses account out of its
     * host's `reclassesAccounts` array and into that host's `accounts` array,
     * stamped with `isReclassAccount: true`.
     *
     * Why
     * ---
     * Phantoms were originally a parallel array rendered after the imported
     * accounts (`fliRowEntries`). That kept them out of every mechanism that
     * operates on `accounts`: `GroupingPathHelper` never stamped them with a
     * `sortingId`, `applyGroupingAndSummarize` never saw them, sorting state
     * never captured them, and the checkbox selection key
     * (`sortingId` + identifier) collapsed for them. Consequence: a manual
     * account could not be grouped, ordered, dragged or selected next to an
     * imported one.
     *
     * Hoisting them into `accounts` *before* the path/grouping/sorting
     * pipeline runs makes all of that work with no further changes, because
     * from that point on a phantom simply IS an account.
     *
     * Invariant
     * ---------
     * This MOVES (never copies) and then empties the source array, so every
     * entry lives in exactly one of the two arrays. Consumers that read both
     * (`recalculateGroupSummaries`, `FlisMapper`, `FlisAccountsHelper`) can
     * therefore keep reading both without double-counting.
     *
     * Scope
     * -----
     * Only entries whose `type` is NOT `AccountType.RECLASSES` are hoisted.
     * That is exactly the set stamped by `applyNewReclassesAccounts`
     * (`BALANCE` on the BS, `INCOME` on the IS). Legacy v0 reclasses accounts
     * (`AccountType.RECLASSES`, owned by the old "Umgliederungen & Stille
     * Reserven" tab) stay where they are and keep rendering through
     * `fliRowEntries` exactly as before — those reports have the transactional
     * feature disabled anyway.
     *
     * Idempotent: a second pass finds the arrays already empty.
     *
     * Mutates in place — only ever call it on a clone.
     */
    public static hoistReclassesAccounts(group: IAccountMappingTemplateGroup): void {
        if (group == null) return;

        const isGroup = (item: any): boolean => item != null && 'sortedItems' in item;

        /** Move the hoistable entries of a single FLI / SubFLI node. */
        const hoistNode = (node: any): void => {
            if (node == null || !Array.isArray(node.reclassesAccounts) || node.reclassesAccounts.length === 0) {
                return;
            }
            const hoistable = node.reclassesAccounts.filter(
                (ra: any) => ra != null && ra.type !== AccountType.RECLASSES,
            );
            if (hoistable.length === 0) return;

            if (!Array.isArray(node.accounts)) node.accounts = [];
            for (const ra of hoistable) {
                ra.isReclassAccount = true;
                // Appended at the end of the container; any user-defined order
                // is re-applied afterwards from the persisted sorting state.
                node.accounts.push(ra);
            }
            node.reclassesAccounts = node.reclassesAccounts.filter(
                (ra: any) => ra != null && ra.type === AccountType.RECLASSES,
            );
        };

        const visitFli = (fli: any): void => {
            hoistNode(fli);
            // SubFLIs / of-that-items carry their own reclassesAccounts.
            // Snapshot the list first: hoistNode mutates `fli.accounts`.
            const children = Array.isArray(fli?.accounts) ? [...fli.accounts] : [];
            for (const child of children) {
                if (child == null) continue;
                if (child.type === ItemType.SUB_FLI || child.type === ItemType.OF_THAT_ITEM) {
                    hoistNode(child);
                }
            }
        };

        /**
         * `items[]` and `sortedItems[]` hold INDEPENDENT copies of the same FLI
         * once the tree has been `cloneDeep`-d (see the note in
         * `applyNewReclassesAccounts`), so both have to be visited. When they
         * still share references the second visit is a no-op — hoisting is
         * idempotent.
         */
        const visitGroup = (g: any): void => {
            if (g == null) return;
            for (const list of [g.items, g.sortedItems]) {
                if (!Array.isArray(list)) continue;
                for (const item of list) {
                    if (item == null) continue;
                    if (item.type === ItemType.FLI) visitFli(item);
                    else if (isGroup(item)) visitGroup(item);
                }
            }
            if (Array.isArray(g.groups)) {
                for (const sub of g.groups) visitGroup(sub);
            }
        };

        visitGroup(group);
    }

    /**
     * Recursively traverses the template tree and applies masking (renaming) to all accounts
     */
    public static applyMaskingToAllAccounts(
        group: IAccountMappingTemplateGroup,
        renamedAccounts: Record<string, string>
    ): void {
        if (!group || !renamedAccounts) return;

        const applyMaskingToAccount = (account: any) => {
            if (!account) return;
            // Most specific first: a rename stored for THIS row wins over one stored under the
            // 2-part identifier, which (for a duplicate pair) stands for every instance and is the
            // only form reports saved before row identity carry.
            for (const identifier of MaskingHelper.getIdentifierCandidates(account)) {
                if (renamedAccounts[identifier]) {
                    account.name = renamedAccounts[identifier];
                    return;
                }
            }
        };

        const traverseItem = (item: any) => {
            if (!item) return;

            // Handle groups recursively
            if (item.sortedItems) {
                item.sortedItems.forEach((child: any) => traverseItem(child));
            }
            // Handle FLIs
            else if (item.type === ItemType.FLI) {
                if (item.accounts) {
                    item.accounts.forEach((account: any) => {
                        // Handle SUB_FLI and OF_THAT_ITEM
                        if (account.type === 'SUB FLI' || account.type === 'OF_THAT_ITEM') {
                            if (account.accounts) {
                                account.accounts.forEach((subAccount: any) => applyMaskingToAccount(subAccount));
                            }
                        } else {
                            applyMaskingToAccount(account);
                        }
                    });
                }
            }
        };

        traverseItem(group);
    }

    /**
     * Recursively traverses the template tree and applies sorting to each FLI item
     */
    public static applySortingToAllItems(
        group: IAccountMappingTemplateGroup,
        sortingState: { [key: string]: string[] }
    ): void {
        if (!group) return;

        // Process all sorted items in this group
        if (group.sortedItems) {
            group.sortedItems.forEach((item: any) => {
                if (item.groups) {
                    // It's a group, recurse
                    this.applySortingToAllItems(item, sortingState);
                } else if (item.type === ItemType.FLI) {
                    // It's a FLI, apply sorting
                    GroupingPathHelper.applySortingStateToItem(item, sortingState);
                }
            });
        }

        // Also process items directly if they exist
        if (group.items) {
            group.items.forEach((item: any) => {
                if (item.type === ItemType.FLI) {
                    GroupingPathHelper.applySortingStateToItem(item, sortingState);
                }
            });
        }

        // Process nested groups
        if (group.groups) {
            group.groups.forEach((nestedGroup: IAccountMappingTemplateGroup) => {
                this.applySortingToAllItems(nestedGroup, sortingState);
            });
        }
    }
}